import React, { useEffect, useState } from "react";
import { Button, Input, message, ColorPicker, Form } from "antd";
import { authSVC } from "../../services";
import { useB2BContext } from "../../context/ContextB2B";
import { useNavigate, useLocation } from "react-router-dom";
import "./EditDepartments.scss";

const departmentColors = [
  "#479751", "#CB2E89", "#9EC756", "#D476AA",
  "#F5EE56", "#65529C", "#EBB949", "#84559D",
  "#E6893C", "#233264", "#D56135", "#9891C2",
  "#D94834", "#3170B5", "#B93347", "#4FA1A5",
];

function EditDepartments() {
  const { getUserByID } = useB2BContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [allDepartments, setAllDepartments] = useState<any[]>([]);
  const [departmentName, setDepartmentName] = useState<string>("");
  const [editDepartment, setEditDepartment] = useState<any>(null);
  const [colorValue, setColorValue] = useState<string>("");
  const [form] = Form.useForm();

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const departmentId = queryParams.get("did");

  const getDepartments = () => {
    authSVC
      .getCompanyDepartments(getUserByID?.company)
      .then((res: any) => {
        const departments = res.data.data;
        setAllDepartments(departments);

        const found = departments.find((d: any) => d._id === departmentId);
        if (found) {
          setEditDepartment(found);
          setDepartmentName(found.name);
          setColorValue(found.color || "");
          form.setFieldsValue({ name: found.name });
        }
      })
      .catch((err: any) => message.error(err.message));
  };

  useEffect(() => {
    getDepartments();
  }, []);

  const onFinish = () => {
    if (!editDepartment) return;
    setLoading(true);

    authSVC
      .editDepartment({
        companyName: getUserByID?.company,
        department_id: editDepartment._id,
        department_name: departmentName,
        department_color: colorValue,
      })
      .then(() => {
        message.success("Department updated successfully!");
        form.resetFields();
        setColorValue("");
        navigate("/departments");
      })
      .catch((err: any) => message.error(err.message))
      .finally(() => setLoading(false));
  };

  const nameValidation = (_: any, value: any) => {
    const regex = /^[A-Za-z\s]+$/;
    if (!value || regex.test(value)) return Promise.resolve();
    return Promise.reject(
      new Error("Please enter a valid name with only letters.")
    );
  };

  const handleSwatchClick = (color: string) => {
    setColorValue(color);
  };

  const handleCustomColor = (color: any) => {
    setColorValue(color.toHexString());
  };

  return (
    <div
      className="edit-dept"
      style={{ "--active-color": colorValue || "#3170B5" } as React.CSSProperties}
    >
      <div className="edit-dept__accent-bar" />

      {/* Back link */}
      <button
        type="button"
        className="edit-dept__back"
        onClick={() => navigate("/departments")}
      >
        ← Back to Departments
      </button>

      <h1 className="edit-dept__title">Edit Department</h1>
      <p className="edit-dept__subtitle">
        Update the name or colour for this department.
      </p>

      {/* Live color preview */}
      <div className={`edit-dept__preview${colorValue ? " edit-dept__preview--active" : ""}`}>
        <span
          className="edit-dept__preview-dot"
          style={{ backgroundColor: colorValue || undefined }}
        />
        <span className="edit-dept__preview-label">
          {colorValue ? "Department colour selected" : "No colour selected yet"}
        </span>
        {colorValue && (
          <span className="edit-dept__preview-hex">{colorValue.toUpperCase()}</span>
        )}
      </div>

      <Form
        name="edit_department"
        initialValues={{ remember: true }}
        onFinish={onFinish}
        form={form}
      >
        <Form.Item
          name="name"
          rules={[
            { required: true, message: "Please enter a department name." },
            { validator: nameValidation },
          ]}
        >
          <Input
            type="text"
            placeholder="e.g. Engineering, Marketing…"
            className="edit-dept__input"
            onChange={(e: any) => setDepartmentName(e?.target?.value)}
          />
        </Form.Item>

        <p className="edit-dept__section-label">Pick a colour</p>

        <Form.Item name="color" style={{marginBottom:0}}>
          <div className="edit-dept__color-grid">
            {departmentColors.map((color, index) => (
              <button
                key={index}
                type="button"
                className={`edit-dept__swatch${colorValue === color ? " edit-dept__swatch--active" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => handleSwatchClick(color)}
                aria-label={`Select colour ${color}`}
              />
            ))}
          </div>

          <div className="edit-dept__custom-row">
            <span className="edit-dept__custom-label">Custom</span>
            <ColorPicker
              value={colorValue || undefined}
              onChangeComplete={handleCustomColor}
            />
          </div>
        </Form.Item>

        <p className="edit-dept__hint">
          Colour changes will reflect across all employee profiles assigned to
          this department.
        </p>

        <Button
          className="edit-dept__submit"
          htmlType="submit"
          loading={loading}
        >
          Save Changes
        </Button>
      </Form>
    </div>
  );
}

export default EditDepartments;